import React, { useState, useMemo, useCallback } from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Badge } from "../../design/badge";
import { useAttorney } from "../../contexts/AttorneyContext";
import CreateAttorneyButton from "./components/CreateAttorneyButton";
import AttorneyGrid from "./components/AttorneyGrid";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import EmptyStateView from "../../components/EmptyStateView";
import { Navbar, NavbarItem } from "../../design/navbar";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ViewToggle, { ViewMode } from "../../components/ViewToggle";
import { getAttorneyDisplayName } from "@visitproof/types";

const AttorneyListPage: React.FC = () => {
  const { attorneys, loading, error } = useAttorney();
  const [status, setStatus] = useState<"inactive" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  // Memoized filtered attorneys
  const filteredAttorneys = useMemo(() => {
    let filtered = attorneys;

    // Status filter
    if (status === "inactive") {
      filtered = filtered.filter((attorney) => attorney.status === "inactive");
    } else {
      filtered = filtered.filter((attorney) => attorney.status === "active");
    }

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((attorney) => {
        const fullName = `${attorney.firstName} ${attorney.lastName}`.toLowerCase();
        const email = attorney.email?.toLowerCase() || "";
        const firmName = attorney.firmName?.toLowerCase() || "";
        const phoneNumber = attorney.phoneNumber?.toLowerCase() || "";

        return (
          fullName.includes(searchLower) ||
          email.includes(searchLower) ||
          firmName.includes(searchLower) ||
          phoneNumber.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [attorneys, status, searchTerm]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setSearchTerm("");
  }, []);

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const hasActiveFilters = Boolean(searchTerm.trim());
  const hasAttorneys = attorneys.length > 0;
  const hasFilteredAttorneys = filteredAttorneys.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Attorneys</Heading>
          {hasAttorneys && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {filteredAttorneys.length} of {attorneys.length} attorneys
              {hasActiveFilters && " (filtered)"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasAttorneys && (
            <ViewToggle view={viewMode} onViewChange={setViewMode} />
          )}
          <CreateAttorneyButton />
        </div>
      </div>

      {/* Status Tabs */}
      <Navbar>
        <NavbarItem current={status === null} onClick={() => setStatus(null)}>
          Active
        </NavbarItem>
        <NavbarItem
          current={status === "inactive"}
          onClick={() => setStatus("inactive")}
        >
          Inactive
        </NavbarItem>
      </Navbar>

      {/* Search Filter */}
      {hasAttorneys && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1 sm:max-w-sm">
            <Input
              type="search"
              placeholder="Search attorneys..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              plain
              onClick={clearFilters}
              className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      {!hasAttorneys ? (
        <EmptyStateView
          title="No Attorneys Yet"
          message="Add attorneys to your account to assign them to cases. Attorneys can be reused across multiple cases."
        />
      ) : !hasFilteredAttorneys ? (
        <div className="mt-8 text-center">
          <Heading>No attorneys match your search</Heading>
          <p className="mt-2 text-zinc-500 dark:text-zinc-400">
            Try adjusting your search terms or{" "}
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              clear all filters
            </button>
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <AttorneyGrid attorneys={filteredAttorneys} />
      ) : (
        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Firm</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Cases</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAttorneys.map((attorney) => (
              <TableRow key={attorney.id} href={`/attorney/${attorney.id}`}>
                <TableCell className="font-medium">
                  {getAttorneyDisplayName(attorney)}
                </TableCell>
                <TableCell className="text-zinc-500 dark:text-zinc-400">
                  {attorney.firmName || "—"}
                </TableCell>
                <TableCell>{attorney.phoneNumber || "—"}</TableCell>
                <TableCell>{attorney.email || "—"}</TableCell>
                <TableCell>
                  <Badge color={attorney.activeCaseCount ? "blue" : "zinc"}>
                    {attorney.activeCaseCount || 0} active
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default AttorneyListPage;
